import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';
import { muteFocusRing } from '@theme/utilities';

/**
 * `<edd-drawer-content>` — the body of the Estimated delivery drawer, and the
 * bridge to the app that owns everything inside it (snippets/edd-drawer.liquid,
 * Figma nodes 6400:93899 and 6350:108439).
 *
 * Neither the estimate card on the PDP nor the form in the drawer is ours. Both
 * belong to the Expected Delivery Day app block
 * (`expected_delivery_day_prod_edd_name_*` in templates/product.json), which
 * ships its own centred modal: a fixed `#edd-entire-modal` overlay wrapping an
 * `#edd-modal-content` card, shown and hidden by toggling `.edd-modal-open` /
 * `.edd-modal-close` on the overlay. We can't edit a line of it.
 *
 * Two facts about that app are what make this component small. Every lookup it
 * makes is a `document.getElementById`, so its controls keep working from
 * anywhere in the document; and it rebuilds its markup wholesale — the estimate
 * line in `updateBlock()`, the form in `updateModal()` — or in place, since
 * `changeMethod` appends its validation error with `innerHTML +=`, re-parsing
 * every child. Nothing we put inside either survives on its own.
 *
 * So the form is adopted rather than rebuilt: `#edd-modal-content` is moved into
 * this element, restyled as the drawer's content, and the overlay it came from
 * is hidden by CSS. The card is dressed in place. Both are re-done after every
 * rebuild the observer sees, and every step is idempotent. The app's trigger,
 * its inline `onclick="changeMethod('POSTAL')"`, its postal-code validation and
 * its error message all keep running untouched — nothing here calls into the
 * app or reimplements any of its logic.
 *
 * What the app's rebuilds do lose is the state of its own two fields, so those
 * are snapshotted on submit and written back afterwards (`#restoreSubmitted`) —
 * for the page view only, so a reload starts again from the app's defaults.
 *
 * Open state is the one thing this component takes over outright, because the
 * app's signal for it isn't trustworthy enough to mirror in both directions:
 *   - Opening is driven by a click on the card, which is the whole trigger in
 *     Figma rather than just the app's link. `.edd-modal-open` is never read as
 *     a request to open — the app's server markup ships with that class, and a
 *     variant change morphs it back onto the overlay, either of which would pop
 *     the drawer open unbidden. A stale one is corrected instead.
 *   - Closing is mirrored from the class, which is how the app closes its own
 *     modal after a successful submit, and written back whenever the drawer
 *     closes on its own (Escape, backdrop, close button) so the app's next
 *     `classList.add` is a real mutation rather than a no-op on a class that
 *     never came off.
 */

/** The app's fixed overlay: where the form is built, and the open-state signal. */
const OVERLAY_ID = 'edd-entire-modal';
/** The app's form, adopted into this element. */
const CONTENT_ID = 'edd-modal-content';
/** The app's estimate line, which holds the card. */
const BLOCK_ID = 'id-edd-container';
/** The card itself: the estimate on one side, the CTA on the other. */
const CARD_SELECTOR = '.edd-container';
/** The card's two clusters, which take the theme's CTA 1 and Body 2 type styles. */
const LABEL_SELECTOR = '.edd-delivery';
/** The "Enter Location" / "Change position" span the app opens its modal with. */
const TRIGGER_ID = 'modalButtonShow';
/** The app's estimate-accuracy tooltip, which opens nothing. */
const TOOLTIP_SELECTOR = '.info-tooltip-wrapper';
/** The country select and postal-code field, both bare elements with no wrapper. */
const SELECT_ID = 'stateSelect';
const INPUT_ID = 'postalCodeInput';
/** The submit button — always the first of the form's buttons in DOM order. */
const SUBMIT_ID = 'updatePostalCode';
/** The validation message the app appends to the form, and removes on retry. */
const ERROR_ID = 'invalid-edd-input';

const OPEN_CLASS = 'edd-modal-open';
const CLOSED_CLASS = 'edd-modal-close';

/**
 * The form's values at the moment the app's submit button was pressed.
 *
 * @typedef {object} SubmittedLocation
 * @property {string} country - The selected country code, e.g. "ITA".
 * @property {string} postalCode - The postal code as typed, empty included.
 */

class EddDrawerContent extends HTMLElement {
  /** @type {HTMLElement | null} */
  #overlay = null;

  /** @type {HTMLElement | null} */
  #block = null;

  /** @type {MutationObserver | undefined} */
  #observer;

  /**
   * Held rather than re-queried: `closest` returns null once we're detached,
   * which would leave the listeners below on a drawer we never cleaned up.
   * @type {import('@theme/theme-drawer').ThemeDrawer | null}
   */
  #drawer = null;

  /**
   * The fields as they stood the last time the shopper pressed the app's submit
   * button, or null until they have. Kept because the app drops both across the
   * rebuild that follows — see `#restoreSubmitted`.
   * @type {SubmittedLocation | null}
   */
  #submitted = null;

  connectedCallback() {
    this.#overlay = document.getElementById(OVERLAY_ID);
    this.#block = document.getElementById(BLOCK_ID);
    this.#drawer = /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));

    // No app block on the page — uninstalled, disabled, or dropped from the
    // template. The drawer stays an empty closed dialog and nothing else here
    // runs.
    if (!this.#overlay || !this.#drawer) return;

    this.#observer = new MutationObserver(() => this.#sync());
    // childList: `updateModal()` replaced the form. class: the app opened or
    // closed its modal.
    this.#observer.observe(this.#overlay, { childList: true, attributeFilter: ['class'] });
    // Our own subtree: `innerHTML +=` re-parsed the form's children, so the
    // decoration has to be re-applied to the new nodes.
    this.#observer.observe(this, { childList: true, subtree: true });

    if (this.#block) {
      // The estimate line, watched two ways. childList: `updateBlock()` re-rendered
      // the trigger. role: a variant change re-renders this section, and morph
      // strips the attributes `#decorateTrigger` added back off the trigger — a
      // pure attribute change, invisible to childList. Filtering on the one
      // attribute we set keeps this off the app's frequent style writes, and
      // `#decorateTrigger` is a no-op once the role is back, so it settles.
      this.#observer.observe(this.#block, { childList: true, subtree: true, attributeFilter: ['role'] });
      // Bound to the line, not the trigger, so both survive the app replacing
      // the trigger underneath them.
      this.#block.addEventListener('keydown', this.#handleTriggerKeydown);
      this.#block.addEventListener('click', this.#handleCardClick, { capture: true });
    }

    this.#drawer.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#drawer.addEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose);
    // Delegated so they outlive every rebuild of the form's controls. The
    // submit snapshot listens in the capture phase, which is what puts it ahead
    // of the button's own inline onclick.
    this.addEventListener('keydown', this.#handleKeydown);
    this.addEventListener('input', this.#handleInput);
    this.addEventListener('click', this.#handleSubmitClick, { capture: true });

    this.#sync();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#block?.removeEventListener('keydown', this.#handleTriggerKeydown);
    this.#block?.removeEventListener('click', this.#handleCardClick, { capture: true });
    this.#drawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#drawer?.removeEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose);
  }

  /**
   * Take whatever the app currently has, then match the drawer to its state.
   * Idempotent from end to end: it runs on connect and again on every mutation
   * either side of the bridge produces, including the ones it makes itself.
   */
  #sync() {
    this.#adoptContent();
    this.#decorateControls();
    this.#restoreSubmitted();
    // After the restore: it writes the field's value without firing `input`.
    this.#syncSubmitEnabled();
    this.#decorateError();
    this.#decorateCard();
    this.#decorateTrigger();
    this.#syncOpenState();
  }

  /**
   * Moves the app's form into the drawer.
   *
   * `replaceChildren` drops the copy adopted last time in the same breath as it
   * moves the new one in, and both halves matter: `updateModal()` builds the new
   * form inside the overlay while ours is still in the drawer, and two
   * `#edd-modal-content` (or two `#stateSelect`) in one document would send
   * every `getElementById` the app makes to the hidden copy instead.
   *
   * When the app finds no estimate to show it empties the overlay instead of
   * rendering a form. That is indistinguishable from the steady state where we
   * hold the only copy, so the last one stays put; the app has emptied its
   * estimate line too by then, leaving the drawer with no trigger to open it.
   */
  #adoptContent() {
    const fresh = this.#overlay?.querySelector(`#${CONTENT_ID}`);

    if (fresh) this.replaceChildren(fresh);
  }

  /**
   * Dresses the app's bare controls as the theme's form fields: the classes the
   * CSS in snippets/edd-drawer.liquid hangs off, the caret wrapper the select
   * needs (the app renders no wrapper of its own), and the accessible names the
   * Figma layout leaves out — it labels both fields by placeholder alone.
   */
  #decorateControls() {
    const select = this.querySelector(`#${SELECT_ID}`);
    const input = this.querySelector(`#${INPUT_ID}`);

    if (select && !select.closest('.edd-drawer__select')) {
      const wrapper = document.createElement('div');
      wrapper.className = 'edd-drawer__select';
      select.replaceWith(wrapper);
      wrapper.appendChild(select);
      wrapper.insertAdjacentHTML(
        'beforeend',
        `<span class="edd-drawer__select-caret svg-wrapper icon-caret" aria-hidden="true">${cloneIcon('edd-drawer-caret')}</span>`,
      );
    }

    select?.setAttribute('aria-label', this.dataset.countryLabel ?? '');
    input?.setAttribute('aria-label', this.dataset.postalCodeLabel ?? '');

    // The app gives its buttons a `btn` class that this theme doesn't define;
    // `.button` is the theme's primary CTA, which is what the design asks for.
    for (const button of this.querySelectorAll('button')) {
      button.classList.add('button', 'edd-drawer__button');
    }
  }

  /**
   * Puts the last submitted values back after the app rebuilds the form, so the
   * drawer reopens on what the shopper last pressed the button with — for the
   * rest of the page view, since the snapshot lives in memory and a reload
   * starts again from the app's own defaults.
   *
   * The app keeps neither field across a rebuild. `updateModal()` re-renders the
   * postal code from its own `value` attribute and `changeMethod`'s error branch
   * re-parses the form with `innerHTML +=`, both of which restore the last
   * *accepted* code and discard what is actually in the field — so clearing it
   * and pressing the button leaves the old code on screen. The select loses its
   * selection outright: `activeModal()` only ever sets Italy, and only while no
   * postal code has been accepted (`method != 'POSTAL'`), so after one it sits
   * on the first option, Albania.
   *
   * With no snapshot yet, the country still falls back to the one the app itself
   * recorded — which is its default, restored from its cookie on a reload —
   * because the alternative is that first option, which the shopper never chose
   * and which the card's estimate isn't based on.
   */
  #restoreSubmitted() {
    const select = this.querySelector(`#${SELECT_ID}`);
    const input = this.querySelector(`#${INPUT_ID}`);
    const country = this.#submitted?.country || appCountry();

    // Never write into a field the shopper is in: a rebuild triggered from
    // anywhere else on the page would otherwise overwrite live typing. The
    // rebuilds this exists for destroy both fields, so focus has already left
    // them by the time it matters.
    if (
      select instanceof HTMLSelectElement &&
      document.activeElement !== select &&
      country &&
      select.value !== country &&
      // Options come from the app's own metafield, so a recorded country can be
      // missing from the list if the merchant has since removed it.
      Array.from(select.options).some((option) => option.value === country)
    ) {
      select.value = country;
    }

    if (
      input instanceof HTMLInputElement &&
      document.activeElement !== input &&
      this.#submitted &&
      input.value !== this.#submitted.postalCode
    ) {
      input.value = this.#submitted.postalCode;
    }
  }

  /**
   * Holds the submit button closed until there is a postal code to submit.
   *
   * The app already treats the field as mandatory — `changeMethod` bails on an
   * empty one — but only once the button has been pressed, and it answers a
   * request for an estimate with a generic "Enter correct data". Disabling the
   * button says the same thing before the click, and takes the app's own
   * unreachable branch out of the picture.
   *
   * Whitespace counts as empty: the app trims the code to '' before calling
   * zippopotam, which then answers for the whole country, so a space would buy
   * a round trip and a "Postal code not found".
   */
  #syncSubmitEnabled() {
    const input = this.querySelector(`#${INPUT_ID}`);
    const submit = this.querySelector(`#${SUBMIT_ID}`);

    if (!(input instanceof HTMLInputElement) || !(submit instanceof HTMLButtonElement)) return;

    // Figma gives the field no label to carry the asterisk the theme's own
    // required fields use, so the requirement is stated to assistive tech only.
    input.setAttribute('aria-required', 'true');
    submit.disabled = input.value.trim() === '';
  }

  /**
   * Mirrors the app's validation message onto the field it belongs to using the
   * theme's own error hook (`aria-invalid` — base.css gives it the red border,
   * tinted fill and single shake from Figma BAS-53) and announces it, so a
   * rejected postal code reads here the way it does in every other theme form.
   */
  #decorateError() {
    const input = this.querySelector(`#${INPUT_ID}`);
    const error = this.querySelector(`#${ERROR_ID}`);

    if (!input) return;

    if (error) {
      error.setAttribute('role', 'alert');
      input.setAttribute('aria-invalid', 'true');
      input.setAttribute('aria-describedby', ERROR_ID);
    } else {
      input.removeAttribute('aria-invalid');
      input.removeAttribute('aria-describedby');
    }
  }

  /**
   * Dresses the estimate card in the theme's own type styles and hover rather
   * than restating either in CSS: CTA 1 for the estimate, Body 2 for the CTA —
   * the pairing the "Find in Store" bar above it uses for the same Figma
   * component — and the hover underline both bars share. The chevron is
   * Figma's, and has to be added: the app renders nothing on that side of the
   * CTA.
   */
  #decorateCard() {
    const card = this.#block?.querySelector(CARD_SELECTOR);

    if (!card) return;

    card.classList.add('hover-underline-group');
    card.querySelector(LABEL_SELECTOR)?.classList.add('cta1--typography');

    const trigger = card.querySelector(`#${TRIGGER_ID}`);

    if (!trigger) return;

    trigger.classList.add('body2--typography', 'hover-underline');
    this.#decorateLocation(trigger);

    if (!trigger.nextElementSibling?.classList.contains('edd-card__chevron')) {
      trigger.insertAdjacentHTML(
        'afterend',
        `<span class="svg-wrapper edd-card__chevron" aria-hidden="true">${cloneIcon('edd-card-chevron')}</span>`,
      );
    }
  }

  /**
   * Names the place the estimate is based on in the card's CTA, in place of the
   * app's fixed "Località".
   *
   * The app does print the location, but in the sentence the card hides (see
   * `.edd-disclaimer` in snippets/edd-drawer.liquid), and for a postal code it
   * only has the country and the code to print: `setCapInfo()` stores no city.
   * So the city comes from the app's own geolocation result when it has one,
   * and otherwise from zippopotam — the service the app already validates the
   * postal code against, whose answer carries the place name it then discards.
   * A code shared by several places names the code instead (see `fetchCity`).
   * Lookups are cached for the page view, so the rebuilds after the first only
   * read the cache. No location set, or no city found: the app's label stays.
   *
   * @param {HTMLElement} trigger
   */
  #decorateLocation(trigger) {
    const lookup = appLocation();

    if (!lookup) return;

    const city = cityCache.get(lookup.key);

    if (city) {
      if (trigger.textContent !== city) trigger.textContent = city;
      return;
    }

    if (city === undefined) {
      cityCache.set(lookup.key, null);
      lookup.resolve().then((name) => {
        cityCache.set(lookup.key, name || '');
        // The app may have rebuilt the card, or moved to another location, while
        // the lookup was in flight; the next sync picks up whatever is current.
        if (name) this.#sync();
      });
    }
  }

  /**
   * The app renders its trigger as a bare `<span>` carrying an `onclick`: not
   * focusable, not announced as a control, and invisible to `<theme-drawer>`,
   * whose focus-return falls back to `[aria-controls="<drawer id>"]`. Four
   * attributes promote it to a button, re-applied after every `updateBlock()`.
   */
  #decorateTrigger() {
    const trigger = this.#block?.querySelector(`#${TRIGGER_ID}`);

    if (!trigger || trigger.getAttribute('role') === 'button') return;

    trigger.setAttribute('role', 'button');
    trigger.setAttribute('tabindex', '0');
    trigger.setAttribute('aria-haspopup', 'dialog');
    if (this.#drawer?.id) trigger.setAttribute('aria-controls', this.#drawer.id);
  }

  /** Reconciles the drawer with the class the app last wrote on its overlay. */
  #syncOpenState() {
    const drawer = this.#drawer;
    const overlay = this.#overlay;

    if (!drawer || !overlay) return;

    const appSaysOpen = overlay.classList.contains(OPEN_CLASS) && !overlay.classList.contains(CLOSED_CLASS);

    // Both sides write this class, so the equality check is what stops the
    // mirror from bouncing: a write we made ourselves describes the state the
    // drawer is already in and stops here.
    if (appSaysOpen === drawer.isOpen) return;

    // The app closed its own modal — a successful postal-code submit does this
    // right before it refetches the estimate.
    if (drawer.isOpen) {
      drawer.close();
      return;
    }

    // The class says open while the drawer isn't, which is never a request to
    // open (see the contract at the top of this file): it's the app's server
    // markup, which ships `.edd-modal-open`, or a section morph putting it back
    // on a variant change. Correct it instead of following it.
    this.#setAppOpen(false);
  }

  /**
   * @param {boolean} open
   */
  #setAppOpen(open) {
    this.#overlay?.classList.toggle(OPEN_CLASS, open);
    this.#overlay?.classList.toggle(CLOSED_CLASS, !open);
  }

  #handleDrawerOpen = () => this.#setAppOpen(true);

  #handleDrawerClose = () => this.#setAppOpen(false);

  /**
   * Keeps the submit button in step with the field as it is typed into.
   *
   * @param {Event} event
   */
  #handleInput = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.id === INPUT_ID) this.#syncSubmitEnabled();
  };

  /**
   * Snapshots both fields as the shopper presses submit — the app is about to
   * rebuild the form from its own state and lose them. Runs in the capture
   * phase, so it reads the fields before the button's inline onclick touches
   * anything. See `#restoreSubmitted`.
   *
   * @param {MouseEvent} event
   */
  #handleSubmitClick = (event) => {
    const target = event.target;

    if (!(target instanceof HTMLElement) || !target.closest(`#${SUBMIT_ID}`)) return;

    const select = this.querySelector(`#${SELECT_ID}`);
    const input = this.querySelector(`#${INPUT_ID}`);

    this.#submitted = {
      country: select instanceof HTMLSelectElement ? select.value : '',
      postalCode: input instanceof HTMLInputElement ? input.value : '',
    };
  };

  /**
   * The app's form is not a `<form>` element — it submits from the button's
   * inline onclick — so Enter in the postal-code field would otherwise do
   * nothing.
   *
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.key !== 'Enter' || !(event.target instanceof HTMLInputElement)) return;

    event.preventDefault();
    /** @type {HTMLElement | null} */ (this.querySelector(`#${SUBMIT_ID}`))?.click();
  };

  /**
   * Keyboard activation for the trigger span, matching the button role
   * `#decorateTrigger` gives it. Bound to the estimate line rather than the span
   * so it survives the app re-rendering the span underneath it.
   *
   * @param {KeyboardEvent} event
   */
  #handleTriggerKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (!(event.target instanceof HTMLElement) || event.target.id !== TRIGGER_ID) return;

    // Space would scroll the page; the click runs the app's own onclick, which
    // opens the modal the only way the app knows how.
    event.preventDefault();
    event.target.click();
  };

  /**
   * The whole card opens the drawer, not just the app's link inside it — that's
   * what the chevron in Figma promises. The app's own span keeps its onclick and
   * stays the labelled control for assistive tech and the keyboard; this is the
   * pointer affordance around it.
   *
   * @param {MouseEvent} event
   */
  #handleCardClick = (event) => {
    const target = event.target;

    if (!(target instanceof HTMLElement) || target.closest(TOOLTIP_SELECTOR)) return;

    // Focus first: `<theme-drawer>` returns focus to whatever was focused when
    // it opened, and a click doesn't reliably focus a `tabindex` span — Safari
    // never does. Runs in the capture phase, so both land before the app's own
    // onclick writes its class.
    const trigger = /** @type {HTMLElement | null} */ (this.#block?.querySelector(`#${TRIGGER_ID}`));

    trigger?.focus();
    // A pointer click (`detail` counts clicks; the keyboard path's synthetic
    // click has none) never asked for a ring, but WebKit paints one on a
    // programmatic focus after a tap — which the drawer would then read as a
    // keyboard trigger and ring again on the way out.
    if (event.detail > 0) muteFocusRing(trigger);
    this.#drawer?.open();
  };
}

/**
 * One of the icons Liquid inlined into snippets/edd-drawer.liquid, so the markup
 * comes from the theme's own SVG asset rather than being hand-written here.
 *
 * @param {string} id - The template's id.
 * @returns {string}
 */
function cloneIcon(id) {
  return document.getElementById(id)?.innerHTML ?? '';
}

/**
 * The country the app itself has on record — set when a postal code is
 * submitted, and restored from its `edd_position` cookie on the next page load.
 * Empty whenever no manual location is in play, which is exactly when the app's
 * own Italy default should stand.
 *
 * Read as a bare identifier because the app declares it as a top-level `let`,
 * which lands in the global lexical scope rather than on `window` (typed in
 * assets/global.d.ts). Guarded, since it belongs to an app that could rename or
 * drop it — in which case this quietly leaves the select as the app built it.
 *
 * @returns {string}
 */
function appCountry() {
  try {
    return typeof eddUserSetting === 'object' ? String(eddUserSetting?.country ?? '') : '';
  } catch {
    return '';
  }
}

/**
 * City names by location key: a string once resolved (empty when there is none
 * to show), null while the lookup is in flight.
 *
 * @type {Map<string, string | null>}
 */
const cityCache = new Map();

/**
 * The location the app's current estimate is based on, as a cache key and a
 * way to resolve its city — or null when the estimate rests on the shopper's
 * IP country, which names no place. Guarded like `appCountry`.
 *
 * @returns {{ key: string, resolve: () => Promise<string> } | null}
 */
function appLocation() {
  try {
    if (typeof eddUserSetting !== 'object' || !eddUserSetting || eddUserSetting.method === 'IPSTATE') return null;

    const address = eddUserSetting.chosenShippingInfo?.destination_address;

    if (address?.city) {
      const city = String(address.city);
      return { key: `city:${city}`, resolve: () => Promise.resolve(city) };
    }

    // The app keys its metafield by the ISO-3 code the select uses; zippopotam
    // wants the ISO-2 one, and the app trims the code to its first word too.
    const shortCode = eddUserSetting.metafield?.find((meta) => meta.code === address?.country)?.shortCode;
    const postalCode = String(address?.postal_code ?? '')
      .trim()
      .split(' ')[0];

    if (!shortCode || !postalCode) return null;

    return { key: `${shortCode}/${postalCode}`, resolve: () => fetchCity(shortCode, postalCode) };
  } catch {
    return null;
  }
}

/**
 * @param {string} country - ISO-2 country code.
 * @param {string} postalCode
 * @returns {Promise<string>} The place the code belongs to; the code itself when
 *   it is shared by several places; '' on any failure.
 */
async function fetchCity(country, postalCode) {
  try {
    const response = await fetch(
      `https://zippopotam.us/${encodeURIComponent(country)}/${encodeURIComponent(postalCode)}`,
    );

    if (!response.ok) return '';

    const data = await response.json();
    const places = Array.isArray(data?.places) ? data.places : [];
    const names = new Set(places.map((place) => String(place?.['place name'] ?? '').trim()).filter(Boolean));

    // Outside the big cities an Italian postal code often covers a dozen towns
    // or more (84010: Amalfi coast; 38050: Valsugana), listed in no meaningful
    // order. The estimate is the same for all of them — the app computes it
    // from the code alone — so the card names the code rather than guess a town
    // the shopper is most likely not in.
    if (names.size > 1) return postalCode;

    return names.values().next().value ?? '';
  } catch {
    return '';
  }
}

if (!customElements.get('edd-drawer-content')) {
  customElements.define('edd-drawer-content', EddDrawerContent);
}
